import {
  ComboBoxState,
  ComboBoxStateOptions,
  useComboBoxState as originalUseComboBoxState,
} from '@react-stately/combobox';
import { Collection, Node } from '@react-types/shared';
import { SelectProps } from './SelectProps';
import { SearchableSelectProps } from './SearchableSelect';

/**
 * A wrapper to adjust some details:
 * - When an attempt to commit the value happens (e.g. by pressing `Enter`),
 *   if no option is focused, select the first option.
 * - When there is a currently selected key and dropdown opens, sets that as focused key
 *   The same thing works out of the box for Select for some reason, but we need to manually
 *   do it here.
 * - adjusts the isOpen state, so that the dropdown is closed when there are no options unless
 *   there is a loading state.
 */
export const useComboBoxState = <T extends object>({
  loadingState,
  ...options
}: ComboBoxStateOptions<T> &
  Pick<SearchableSelectProps<T>, 'isOpen'> & {
    loadingState?: SelectProps<unknown>['loadingState'];
  }): ComboBoxState<T> => {
  const state = originalUseComboBoxState(options);
  const isOpen = options.isOpen ?? state.isOpen;
  return {
    ...state,
    isOpen:
      isOpen &&
      (state.collection.size > 0 ||
        Boolean(loadingState) ||
        Boolean(options.allowsEmptyCollection)),
    toggle: (...args) => {
      if (
        state.selectedKey != null &&
        state.selectionManager.focusedKey == null
      ) {
        state.selectionManager.setFocusedKey(state.selectedKey);
      }

      return state.toggle(...args);
    },
    commit: () => {
      const firstItemInCollection = firstItem(
        state.collection,
        state.collection
      );
      if (
        state.selectionManager.focusedKey == null &&
        firstItemInCollection != null
      ) {
        state.selectionManager.setFocusedKey(firstItemInCollection.key);
      }
      return state.commit();
    },
  };
};

const firstItem = <T,>(
  collection: Collection<Node<T>>,
  nodes: Iterable<Node<T>>
): Node<T> | null => {
  if (!collection.size) {
    return null;
  }
  const [firstNode, ...restOfNodes] = Array.from(nodes);
  if (firstNode.type === 'item') {
    return firstNode;
  } else if (firstNode.type === 'section' && firstNode.hasChildNodes) {
    const firstItemInChildren = firstItem(collection, firstNode.childNodes);
    return firstItemInChildren === null
      ? firstItem(collection, restOfNodes)
      : firstItemInChildren;
  } else {
    return firstItem(collection, restOfNodes);
  }
};
